import React from "react";
import { useRecoilState } from "recoil";
import { useHistory, useParams, useLocation } from "react-router-dom";
import { groupedFixturesState } from "../../../Atoms/fixturesAtom";
import { useFixturesQuery } from "../../../hooks/useFixturesQuery";
import Loading from "../../Loading";
import EmptyState from "../../EmptyState";

// This component handles the logic for processing fixtures data
// and setting the grouped fixtures state
// It also redirects to the first fixture if no fixture ID is provided (e.g. /event-management)

export const getFixtureDate = (fixture) => {
  const date = new Date(`${fixture}Z`);
  let year = date.getFullYear();
  let month = date.getMonth();
  let day = date.getDate();

  return new Date(year, month, day);
};

const processFixturesData = (fixtureData) => {
  const enhancedFixtureData = {
    ...fixtureData,
    items: fixtureData.items
      .filter(
        (item) => item.leagueId !== "BC4EAC22-C959-4FF1-8087-5DFA9A024992",
      )
      .map((item) => ({
        ...item,
        fixtureDate: getFixtureDate(item.fixture_start_date_time),
      })),
  };

  const uniqueFixtureDates = enhancedFixtureData.items
    .map((fixture) => fixture.fixtureDate)
    .filter(
      (date, i, self) =>
        self.findIndex((d) => d.getTime() === date.getTime()) === i,
    );

  const groupedFixturesByDay = uniqueFixtureDates.map((date) => ({
    date,
    items: enhancedFixtureData.items.filter(
      (fixture) => fixture.fixtureDate.getTime() === date.getTime(),
    ),
    itemsPerLeague: enhancedFixtureData.items
      .filter((fixture) => fixture.fixtureDate.getTime() === date.getTime())
      .reduce((acc, value) => {
        if (!acc[value.leagueId]) {
          acc[value.leagueId] = [];
        }
        acc[value.leagueId].push(value);
        return acc;
      }, {}),
  }));
  return {
    ...enhancedFixtureData,
    uniqueFixtureDates,
    groupedFixturesByDay,
  };
};

export const FixturesProvider = ({ children }) => {
  const { data, isLoading, isError } = useFixturesQuery();
  const history = useHistory();
  const location = useLocation();
  const { id } = useParams();
  const [_, setGroupedFixtures] = useRecoilState(groupedFixturesState);

  React.useEffect(() => {
    if (data) {
      const processedData = processFixturesData(data);
      setGroupedFixtures(processedData);

      // If no fixtures are available, redirect to the first fixture
      if (
        processedData.groupedFixturesByDay &&
        processedData.groupedFixturesByDay.length > 0
      ) {
        // Find today's date group, or the nearest future date
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const todayIndex = processedData.groupedFixturesByDay.findIndex(
          (group) => group.date >= today,
        );
        const defaultGroup =
          todayIndex !== -1
            ? processedData.groupedFixturesByDay[todayIndex]
            : processedData.groupedFixturesByDay[
                processedData.groupedFixturesByDay.length - 1
              ];
        const defaultFixture = defaultGroup.items[0];

        // Redirect to today's (or nearest future) fixture if no fixture ID is provided
        if (!id && defaultFixture) {
          history.push(`/event-management/${defaultFixture.id}/events`);
        } else if (id) {
          // Check if the fixture with the provided ID exists
          const fixtureExists = processedData.items.some(
            (item) => item.id === id,
          );

          // If the fixture does not exist, redirect to 404
          if (!fixtureExists) {
            history.push("/404");
          }
        }
      }
    }
  }, [data, id, setGroupedFixtures, history, location.pathname]);

  if (isLoading) return <Loading />;
  if (isError) return <EmptyState text="An error occurred" icon="error" />;

  return children;
};
